import { useEffect, useState } from 'react';
import { MobileTopBar } from './MobileTopBar';
import { MobileToolSheet } from './MobileToolSheet';
import { MobilePropertiesSheet } from './MobilePropertiesSheet';
import { MobileCanvasControls } from './MobileCanvasControls';
import { MobileSheetListSheet } from './MobileSheetListSheet';
import { WorkflowCanvas } from '@/components/editor/WorkflowCanvas';
import { ScreenReaderAnnouncer } from '@/components/workflow/ScreenReaderAnnouncer';
import type { MobileSubLayoutProps } from './MobileLayout';

export function MobilePortraitLayout({
  props, minimapData, handleMinimapDataChange, handleRegisterZoomHandlers,
  handleMobileAddBlock, handleZoomIn, handleZoomOut, handleFitView,
}: MobileSubLayoutProps) {
  const [toolSheetOpen, setToolSheetOpen] = useState(false);
  const [propsSheetOpen, setPropsSheetOpen] = useState(false);
  const [sheetListOpen, setSheetListOpen] = useState(false);
  const [topBarDrawerOpen, setTopBarDrawerOpen] = useState(false);

  // Lyssna på gesture-events från Index.tsx — edge-swipe + 3× tap firar
  // dessa custom-events så vi kan toggla state utan att tråda extra
  // handlers genom shell-kedjan.
  //   - open-left-sidebar: edge-swipe från vänster → ToolSheet
  //   - open-right-sidebar: edge-swipe från höger → PropertiesSheet
  //   - open-top-bar: edge-swipe från top → MobileTopBar:s drawer
  //   - open-sheet-list: 3× tap på tom canvas → MobileSheetListSheet
  useEffect(() => {
    const openLeft = () => setToolSheetOpen(true);
    const openRight = () => setPropsSheetOpen(true);
    const openTop = () => setTopBarDrawerOpen(true);
    const openSheetList = () => setSheetListOpen(true);
    window.addEventListener('engicoro:open-left-sidebar', openLeft);
    window.addEventListener('engicoro:open-right-sidebar', openRight);
    window.addEventListener('engicoro:open-top-bar', openTop);
    window.addEventListener('engicoro:open-sheet-list', openSheetList);
    return () => {
      window.removeEventListener('engicoro:open-left-sidebar', openLeft);
      window.removeEventListener('engicoro:open-right-sidebar', openRight);
      window.removeEventListener('engicoro:open-top-bar', openTop);
      window.removeEventListener('engicoro:open-sheet-list', openSheetList);
    };
  }, []);

  return (
    <div className="flex flex-col h-dvh" style={{ paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
      <ScreenReaderAnnouncer message={props.announcement} />
      <MobileTopBar
        drawerOpen={topBarDrawerOpen}
        onDrawerOpenChange={setTopBarDrawerOpen}
        onSave={props.onSave}
        onLoad={props.onLoad}
        onExportPNG={props.onExportPNG}
        onSimulate={props.onSimulate}
        onClear={props.onClear}
        onImportJson={props.onImportJson}
        onCopyJson={props.onCopyJson}
        onHelp={props.onHelp}
        onAiJson={props.onAiJson}
        onAiAssistant={props.onAiAssistant}
        onPreview={props.onPreview}
        onLogin={props.onLogin}
        onCloudWorkspace={props.onCloudWorkspace}
        onShareWorkspace={props.onShareWorkspace}
        onFeatureRequest={props.onFeatureRequest}
        onBugReport={props.onBugReport}
        onDonate={props.onDonate}
        onUndo={props.onUndo}
        onRedo={props.onRedo}
        canUndo={props.canUndo}
        canRedo={props.canRedo}
        isConnected={props.isConnected}
        connectionDebugInfo={props.connectionDebugInfo}
        isDirty={props.isDirty}
      />

      <div className="flex-1 relative overflow-hidden flex flex-col min-h-0" id="canvas" tabIndex={-1}>
        <WorkflowCanvas
          blocks={props.blocks}
          connectors={props.connectors}
          groups={props.groups}
          selectedBlockId={props.selectedBlockId}
          selectedGroupId={props.selectedGroupId}
          connectMode={props.connectMode}
          groupMode={props.groupMode}
          groupSelection={props.groupSelection}
          visibleBlocks={props.visibleBlocks}
          visibleConnectors={props.visibleConnectors}
          collapsedGroups={props.collapsedGroups}
          expandedGroups={props.expandedGroups}
          breadcrumbs={props.breadcrumbs}
          layerPath={props.layerPath}
          onNavigateInto={props.onNavigateInto}
          onNavigateToLevel={props.onNavigateToLevel}
          onSelectBlock={props.onSelectBlock}
          onSelectGroup={props.onSelectGroup}
          onCreateBlock={(type, x, y) => {
            const block = props.onCreateBlock(type, x, y);
            if (props.currentLayerGroupId) {
              props.onUpdateGroup(props.currentLayerGroupId, {
                blockIds: [...(props.groups.find(g => g.id === props.currentLayerGroupId)?.blockIds || []), block.id],
              });
            }
            return block;
          }}
          onUpdateBlockPosition={props.onUpdateBlockPosition}
          onUpdateBlockIcon={props.onUpdateBlockIcon}
          onConnect={props.onConnect}
          onToggleGroupSelection={props.onToggleGroupSelection}
          onCanvasClick={props.onCanvasClick}
          onMinimapDataChange={handleMinimapDataChange}
          onUpdateGroupPosition={props.onUpdateGroupPosition}
          onConvertToLayer={props.onConvertToLayer}
          onUpdateConnectorWaypoints={props.onUpdateConnectorWaypoints}
          onUpdateConnectorLabel={props.onUpdateConnectorLabel}
          canvasInnerRef={props.canvasInnerRef}
          viewOnly={false}
          onRegisterZoomHandlers={handleRegisterZoomHandlers}
          mobileGestures={props.mobileGestures}
        />

        <MobileCanvasControls
          zoom={minimapData?.zoom ?? 1}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFitView={handleFitView}
          onOpenTools={() => setToolSheetOpen(true)}
          onOpenProperties={() => setPropsSheetOpen(true)}
        />
      </div>

      <MobileToolSheet
        open={toolSheetOpen}
        onOpenChange={setToolSheetOpen}
        onAddBlock={handleMobileAddBlock}
        connectMode={props.connectMode}
        groupMode={props.groupMode}
        groupingCount={props.groupSelection.length}
        onToggleConnectMode={props.onToggleConnectMode}
        onToggleGroupMode={props.onToggleGroupMode}
        blocks={props.blocks}
        groups={props.groups}
        currentLayerGroupId={props.currentLayerGroupId}
        onNavigateToLayer={props.onNavigateToLayer}
      />

      <MobilePropertiesSheet
        open={propsSheetOpen}
        onOpenChange={setPropsSheetOpen}
        selectedBlock={props.selectedBlock}
        selectedGroup={props.selectedGroup}
        workflowName={props.workflowName}
        onUpdateWorkflowName={props.onUpdateWorkflowName}
        onUpdateBlock={props.onUpdateBlock}
        onDeleteBlock={props.onDeleteBlock}
        onUpdateGroup={props.onUpdateGroup}
        onDeleteGroup={props.onDeleteGroup}
        blocks={props.blocks}
        connectors={props.connectors}
        groups={props.groups}
        canvasWidth={minimapData?.canvasWidth ?? 4000}
        canvasHeight={minimapData?.canvasHeight ?? 2500}
        viewportWidth={minimapData?.viewportWidth ?? 0}
        viewportHeight={minimapData?.viewportHeight ?? 0}
        scrollLeft={minimapData?.scrollLeft ?? 0}
        scrollTop={minimapData?.scrollTop ?? 0}
        blockDimensions={minimapData?.blockDimensions ?? {}}
        zoom={minimapData?.zoom ?? 1}
        onMinimapNavigate={minimapData?.onNavigate ?? (() => {})}
        onNavigateToGroup={props.onNavigateToLayer}
      />

      {/* Triple-tap → välj sheet direkt utan att skrolla SheetTabBar. */}
      <MobileSheetListSheet
        open={sheetListOpen}
        onOpenChange={setSheetListOpen}
      />
    </div>
  );
}
